import React from 'react';
import './PersonalLoanPrequalResultModuleView.scss';
import SectionContainer from 'components/Containers/SectionContainer';
import PageContainer from 'components/Containers/PageContainer';
import BodyContainer from 'components/Containers/BodyContainer';
import StaticOfferContainer from 'partial/OfferManager/StaticOfferContainer';
import PageLoadingMask from 'components/masks/PageLoadingMask';
import { Disclaimer } from 'components/AdvertiserToolTip/AdvertiserToolTip';
import { Button } from 'components/form/Button/Button';
import { CSTooltip } from 'components/Tooltip/CSToolTip';
import TextFromConfig from 'components/TextFromConfig';
import Label from 'components/form/Label/Label';
import { Row } from 'components/Containers/Row/Row';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import FilterOptionsButton from 'components/FilterOptionsButton';
import { commafy } from 'helpers/number';
import { PERSONAL_LOAN_VERTICAL } from 'types/marketplaceTiles.constants';
import { Link } from 'components/navigation/Link/Link';
import { useConfig } from 'hook/config.hook';

const getSubtitle = (isMarketplace, dueDate, config) => {
  const subtitle = isMarketplace
    ? `Your current offers will expire ${dueDate}`
    : `All offers are available until ${dueDate}. ${config?.offersPage?.PersonalLoan?.preQualifiedSubtitle}`;
  return subtitle;
};

const PrequalTopSection = ({
  userInfo,
  sortBy,
  sortByList,
  sameAmountOffers,
  onSortChange,
  onClickSort,
  sortLoading,
  showAllOffers,
  showAllSameAmountOffers,
  onClickSearchNoPrequal,
  onClickShowAllOffers,
  onClickShowAllSameAmountOffers,
  formLoanAmount,
  isMarketplace,
  ...props
}) => {
  const sameAmtOffers = sameAmountOffers ? sameAmountOffers : [];
  const dueDate = sameAmtOffers.length > 0 ? sameAmtOffers[0].dueDate : '';
  const { config } = useConfig();
  const finalSameAmountOffers =
    sameAmtOffers.length > 3 && !showAllSameAmountOffers
      ? sameAmtOffers.slice(0, 3)
      : sameAmtOffers;

  const numOffersText = sameAmountOffers.length === 1 ? 'offer' : 'offers';
  const topText = isMarketplace
    ? `${userInfo?.firstName}, you’re pre-qualified for ${sameAmountOffers.length} ${numOffersText} for `
    : `Congratulations, ${userInfo?.firstName}! You’re pre-qualified for ${sameAmountOffers.length} ${numOffersText} for `;

  if (sameAmtOffers.length === 0) {
    return '';
  }
  return (
    <FlexContainer className="flex-column">
      <Label
        type="H3 mH4"
        typeHeader="h3"
        className="margB8 m-margB12 m-text-16"
      >
        {topText}
        <span className="noWrapText">
          ${commafy(formLoanAmount)}
          <CSTooltip
            tooltipName="Prequal Offer Tooltip"
            placement="bottomLeft"
            vertical={PERSONAL_LOAN_VERTICAL}
            title={config?.disclaimers?.plPrequalResultTooltip}
            isInline={true}
            overlayClassName="annualIncomeCSTooltip"
            arrowPointAtCenter
          />
        </span>
      </Label>
      <div className="Body-1 margB24 m-text-14">
        {getSubtitle(isMarketplace, dueDate, config)}
      </div>
      {config?.offersPage?.PersonalLoan?.showPHXPrequalDisclaimer && (
        <Disclaimer
          disclosure={
            config?.showPrequalDisclaimer
              ? 'Pre-Qualification Disclosure'
              : 'Advertiser Disclosure'
          }
          disclaimerText={
            config?.showPrequalDisclaimer
              ? config.disclaimers?.plPreAdvertiserDisclosureCopy
              : config.disclaimers?.advertiserDisclosureCopy
          }
          className="padB10 padR4 flex-row"
        />
      )}
      <div className="prequalOfferContainer">
        <StaticOfferContainer
          eventHandler={props.eventHandler}
          datatestid={props.datatestid}
          offerMetadataList={finalSameAmountOffers}
          pagePosition={'Personal Loan Prequal Result'}
          modulePosition={props.modulePosition}
        />
      </div>
      {!showAllSameAmountOffers && sameAmtOffers.length > 3 ? (
        <div className="margLRAuto margT16 margB40 m-margT8 t-Width100">
          <Button
            mrphType="standard"
            className="seeAllButton border-rad4px"
            onClick={onClickShowAllSameAmountOffers}
          >
            See more pre-qualified offers
          </Button>
        </div>
      ) : (
        <div className="margB48 m-margB24"></div>
      )}
    </FlexContainer>
  );
};

const PrequalBottomSection = ({
  sortBy,
  sortByList,
  sameAmountOffers,
  otherAmountOffers,
  onSortChange,
  onClickSort,
  sortLoading,
  showAllOffers,
  onClickSearchNoPrequal,
  onClickShowAllOffers,
  isMarketplace,
  formLoanAmount,
  userInfo,
  config,
  ...props
}) => {
  const hasSameAmountOffers = sameAmountOffers
    ? sameAmountOffers.length > 0
    : false;
  const otherOffers = otherAmountOffers ? otherAmountOffers : [];
  const dueDate = otherOffers.length > 0 ? otherOffers[0].dueDate : '';

  const finalOtherOffers =
    isMarketplace && otherOffers.length > 3 && !showAllOffers
      ? otherOffers.slice(0, 3)
      : otherOffers;

  const otherAmountTitle = isMarketplace
    ? `${userInfo.firstName}, you’re pre-qualified for ${otherOffers.length} offers`
    : `You're pre-qualified for other loan amounts`;

  return (
    <FlexContainer className="flex-column">
      {hasSameAmountOffers && (
        <Label
          type="H3 mH4"
          typeHeader="h3"
          className="margB12 m-margB4 m-text-16"
        >
          You’re also pre-qualified for other loan amounts
        </Label>
      )}
      {!hasSameAmountOffers && (
        <>
          <Label
            type="H3 mH4"
            typeHeader="h3"
            className="margB8 m-margB12 m-text-16"
          >
            {otherAmountTitle}
            <CSTooltip
              tooltipName="Prequal Offer Tooltip"
              vertical={PERSONAL_LOAN_VERTICAL}
              placement="bottom"
              title={
                <TextFromConfig
                  path={TextFromConfig.paths.DISCLAIMER_PL_PREQUAL_RESULT}
                />
              }
              isInline={true}
              overlayClassName="annualIncomeCSTooltip"
            />
          </Label>
          <div className="Body-1 margB12">
            {getSubtitle(isMarketplace, dueDate, config)}
          </div>
        </>
      )}
      <div className="Body-1 margB24">
        <FilterOptionsButton
          type="singleOption"
          initialText={sortBy}
          options={sortByList}
          values={[]}
          onSortBy={e => onSortChange(e)}
          prefix="Sort: "
        />
      </div>
      {config?.offersPage?.PersonalLoan?.showPHXPrequalDisclaimer && (
        <Disclaimer
          disclosure={
            config?.showPrequalDisclaimer
              ? 'Pre-Qualification Disclosure'
              : 'Advertiser Disclosure'
          }
          disclaimerText={
            config?.showPrequalDisclaimer
              ? config.disclaimers?.plPreAdvertiserDisclosureCopy
              : config.disclaimers?.advertiserDisclosureCopy
          }
          className="padB10 padR4 flex-row"
        />
      )}
      {!sortLoading ? (
        <div className="prequalOfferContainer">
          <StaticOfferContainer
            eventHandler={props.eventHandler}
            datatestid={props.datatestid}
            offerMetadataList={finalOtherOffers}
            pagePosition={'Personal Loan Prequal Result'}
            modulePosition={props.modulePosition}
          />
        </div>
      ) : (
        <PageLoadingMask />
      )}
    </FlexContainer>
  );
};

export const ShowtotalCostTerm = ({ className }) => {
  const { config } = useConfig();

  return (
    <div className={`fontGray300 caption ${className}`}>
      {config?.personal_loan?.footer_disclaimer}
    </div>
  );
};

export const PrequalUnifiedResultMarketplaceView = props => {
  const {
    sortLoading,
    showAllOffers,
    onClickSearchNoPrequal,
    onClickShowAllOffers,
    config,
  } = props;

  return (
    <BodyContainer className="plPrequalResult plPrequalUnified plPrequalNoFirstTime margT32 m-margT0">
      <PageContainer>
        <Label
          typeHeader={'h2'}
          className="H1 margB24 m-margT24 m-marglr16 m-margB16 m-text-16"
        >
          Personal Loans
        </Label>
        <Row fullWidth className="prequalDisclaimer">
          {config?.offersPage?.PersonalLoan?.showPHXPrequalDisclaimer ? (
            <Disclaimer showPartnerText vertical={PERSONAL_LOAN_VERTICAL} />
          ) : (
            <Disclaimer
              showPartnerText
              vertical={PERSONAL_LOAN_VERTICAL}
              disclosure={
                config?.showPrequalDisclaimer
                  ? 'Pre-Qualification Disclosure'
                  : 'Advertiser Disclosure'
              }
              disclaimerText={
                config?.showPrequalDisclaimer
                  ? config.disclaimers?.plPreAdvertiserDisclosureCopy
                  : config.disclaimers?.advertiserDisclosureCopy
              }
            />
          )}
        </Row>
        <SectionContainer className="padB24 margB42 m-padB8 m-margB32">
          <Row>
            <PrequalTopSection isMarketplace={true} {...props} />
            <FlexContainer className="flex-column">
              <PrequalBottomSection isMarketplace={true} {...props} />
              {!showAllOffers && !sortLoading && (
                <div className="margLRAuto margTB16 m-margT8 t-Width100">
                  <Button
                    mrphType="standard"
                    className="seeAllButton border-rad4px"
                    onClick={onClickShowAllOffers}
                  >
                    See more pre-qualified offers
                  </Button>
                </div>
              )}
            </FlexContainer>
          </Row>
        </SectionContainer>
        <FlexContainer className="justify-center items-align-center">
          <Link
            to={'/offers/personalLoan'}
            className="otherLink text-center fontBlue100 Body-2 bold margB48 pointer"
            onClickCallback={onClickSearchNoPrequal}
            ariaLabel="Search without pre-qualification"
          >
            Search without pre-qualification
          </Link>
        </FlexContainer>
        <ShowtotalCostTerm className={'margT10 m-margLR16'} />
      </PageContainer>
    </BodyContainer>
  );
};

export const PrequalUnifiedResultView = props => {
  const { sameAmountOffers, formLoanAmount, onClickSearchNoPrequal } = props;
  const sameAmtOffers = sameAmountOffers ? sameAmountOffers : [];
  const hasSameAmountOffers = sameAmtOffers.length > 0;

  return (
    <BodyContainer className="plPrequalResult plPrequalUnified plFirstTime margT32 m-margT0">
      <PageContainer>
        <SectionContainer className="mainSection padB24 margB42 m-padB8 m-margB32">
          {hasSameAmountOffers && (
            <Row className="prequalDisclaimer margT32 margB12 m-margT16">
              <Disclaimer showPartnerText vertical={PERSONAL_LOAN_VERTICAL} />
            </Row>
          )}
          {!hasSameAmountOffers && (
            <>
              <Row>
                <FlexContainer className="flex-column">
                  <Label
                    typeHeader={'h3'}
                    className="H3 margT40 margB40 m-margT20 mH5"
                  >
                    We were unable to find any pre-qualified offers for $
                    {commafy(formLoanAmount)} for you at this time.
                  </Label>
                  <div className="separator margB8 m-margB16"></div>
                </FlexContainer>
              </Row>
              <Row className="prequalDisclaimer disclaimerOtherAmounts">
                <Disclaimer showPartnerText vertical={PERSONAL_LOAN_VERTICAL} />
              </Row>
            </>
          )}
          <Row>
            <div className="FlexContainer flex-column">
              <PrequalTopSection {...props} />
              <PrequalBottomSection {...props} />
            </div>
          </Row>
        </SectionContainer>
        <FlexContainer className="justify-center items-align-center">
          <Link
            to={'/offers/personalLoan'}
            className="otherLink text-center fontBlue100 Body-2 bold margB48 pointer"
            onClickCallback={onClickSearchNoPrequal}
            ariaLabel="Search without pre-qualification"
          >
            Search without pre-qualification
          </Link>
        </FlexContainer>
      </PageContainer>
    </BodyContainer>
  );
};
